import React, { useState, useEffect } from "react";
import { Box, Typography } from "../../../design-system";
import UploadDownloadArea from "./UploadDownloadArea";
import HeroClipDemo from "./HeroClipDemo";
import HomeEtEmbed from "./HomeEtEmbed";
import HeroBackground from "../../landing/hero/HeroBackground";
import logoSite from "../../../../assets/brand/logo_site.svg";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

/**
 * Alternate home-page hero layouts for the layout-experiment toggle. Variant A keeps the
 * original MainSection untouched. B–F reuse the SAME upload component (UploadDownloadArea) and
 * looping demo (HeroClipDemo); only arrangement/headline/CTA differ. Copy + arrangement for
 * D/E/F come from the conversion + design council, modeled on the benefit-hero sites the
 * webinar teacher praised (big benefit headline w/ accent words, one short subhead, clear CTA,
 * strong visual right, clean nav, whitespace).
 */

type Variant = "B" | "C" | "D" | "E" | "F";

// Shorter, benefit-led, no shush emoji (per the copy council).
const SUBHEAD_LONG =
  "We cut every sentence into its own clip. Cherry-pick yours, or let AI.";
const PILLS = ["Speaker", "Emotion", "Length", "Question"];

const Pills: React.FC<{ center?: boolean }> = ({ center }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 10px", justifyContent: center ? "center" : "flex-start" }}>
    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 600, color: "#8d94a8", fontSize: { xs: "12px", md: "14px" }, mr: "2px" }}>Filter by:</Typography>
    {PILLS.map((p) => (
      <Box key={p} sx={{ display: "flex", alignItems: "center", gap: "7px", padding: "5px 12px", borderRadius: "999px", border: "1px solid rgba(230,29,80,0.35)", backgroundColor: "rgba(230,29,80,0.06)" }}>
        <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#e61d50" }} />
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, color: "#c2c7d6", fontSize: { xs: "12px", md: "14px" }, whiteSpace: "nowrap" }}>
          {p}
        </Typography>
      </Box>
    ))}
  </Box>
);

const Subhead: React.FC<{ align: "left" | "center"; children: React.ReactNode; max?: number }> = ({ align, children, max = 560 }) => (
  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, textAlign: align, color: "#c2c7d6", fontSize: { xs: "16px", md: "20px" }, lineHeight: 1.45, maxWidth: `${max}px`, ...(align === "center" ? { mx: "auto" } : {}) }}>
    {children}
  </Typography>
);

const headlineSx = (align: "left" | "center", size: "lg" | "xl" = "lg") => ({
  fontFamily: "Outfit, sans-serif",
  fontWeight: 800,
  textAlign: align,
  lineHeight: 1.04,
  color: "#f3f4f9",
  fontSize: size === "xl" ? { xs: "40px", md: "76px" } : { xs: "34px", md: "60px" },
  letterSpacing: "-0.02em",
});

const A = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ color: "#e61d50", whiteSpace: "nowrap" }}>{children}</Box>
);

// Rotating hero copy — the value prop said a few different ways. Auto-cycles; click a dot to pin.
const HERO_COPY: { head: React.ReactNode; sub: string }[] = [
  { head: <>CherryPick your <A>video clipz.</A></>, sub: "We cut every sentence into its own clip — ready to CherryPick." },
  { head: <>A <A>spreadsheet</A> for your video.</>, sub: "Every sentence becomes a sortable, searchable clip. Filter, pick, export." },
  { head: <><A>Cut everything.</A> Keep the cherry on top.</>, sub: "We slice your whole video into clips — you keep only the best." },
  { head: <>100 clips from <A>one video.</A></>, sub: "Every quotable moment, ready to CherryPick — or hand to the AI." },
  { head: <>Never scrub a <A>transcript</A> again.</>, sub: "Your words become clickable clips. Search a line — get the video." },
  { head: <>Your footage, fully <A>searchable.</A></>, sub: "Filter by speaker, emotion, or a word. Pick the winners in seconds." },
  { head: <>Or let <A>CherryClipz AI</A> cut it.</>, sub: "Describe the reel — the AI filters, scores &amp; picks the clips for you." },
];

const RotatingHero: React.FC = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = window.setInterval(() => setI((x) => (x + 1) % HERO_COPY.length), 4500);
    return () => window.clearInterval(t);
  }, [paused]);
  const c = HERO_COPY[i];
  return (
    <Box onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} sx={{ display: "flex", flexDirection: "column", gap: { xs: "16px", md: "20px" }, alignItems: { xs: "center", md: "flex-start" }, width: "100%" }}>
      <Typography sx={{ ...headlineSx("left"), textAlign: { xs: "center", md: "left" }, minHeight: { md: "124px" } }}>{c.head}</Typography>
      <Box sx={{ textAlign: { xs: "center", md: "left" }, minHeight: { md: "56px" } }}>
        <Subhead align="left">{c.sub}</Subhead>
      </Box>
      <Box sx={{ display: "flex", gap: "7px" }}>
        {HERO_COPY.map((_, n) => (
          <Box key={n} component="button" onClick={() => setI(n)} title="Switch tagline" sx={{ cursor: "pointer", width: n === i ? 24 : 8, height: 8, borderRadius: "999px", border: "none", p: 0, background: n === i ? "#e61d50" : "rgba(255,255,255,0.2)", transition: "all .25s" }} />
        ))}
      </Box>
    </Box>
  );
};

const DemoFrame: React.FC = () => (
  <Box sx={{ width: "100%", borderRadius: "16px", border: "1px solid rgba(230,29,80,0.22)", background: "radial-gradient(120% 100% at 50% 0%, rgba(230,29,80,0.10), transparent 60%)", padding: { xs: "10px", md: "14px" } }}>
    <HeroClipDemo />
  </Box>
);

const scrollToDemo = () => {
  try {
    document.getElementById("cc-hero-demo")?.scrollIntoView({ behavior: "smooth", block: "center" });
  } catch (e) {
    /* ignore */
  }
};

/* ---------------- Existing variants B & C (unchanged behavior) ---------------- */

const VariantBSplit: React.FC = () => (
  <>
    <Box sx={{ width: "100%", maxWidth: "1280px", margin: "0 auto", padding: { xs: "8px 0 24px", md: "24px 0 40px" } }}>
      {/* Grid (not flex) so the upload box NEVER shifts when the rotating copy changes length. */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "center", gap: { xs: "28px", md: "56px" } }}>
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: { xs: "18px", md: "22px" }, alignItems: { xs: "center", md: "flex-start" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img src={logoSite} alt="CherryClipz" width={40} style={{ height: "auto", display: "block" }} />
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, letterSpacing: "0.14em", fontSize: "12px", color: "#8d94a8", textTransform: "uppercase" }}>
              Video → a spreadsheet of clips
            </Typography>
          </Box>
          <RotatingHero />
        </Box>
        <Box sx={{ minWidth: 0, width: "100%", display: "flex", justifyContent: "center", borderRadius: "20px", border: "1px solid rgba(230,29,80,0.25)", background: "radial-gradient(120% 100% at 50% 0%, rgba(230,29,80,0.10), transparent 60%), rgba(255,255,255,0.02)", padding: { xs: "20px 12px", md: "32px 24px" } }}>
          <UploadDownloadArea />
        </Box>
      </Box>
    </Box>
    {/* Bridge: CherryPick yourself OR chat with CherryClipz AI — between the hero and the spreadsheet */}
    <Box sx={{ width: "100%", maxWidth: "1680px", margin: "0 auto", mt: { xs: "18px", md: "28px" } }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" }, alignItems: "stretch", gap: { xs: "12px", md: "16px" } }}>
        {/* CherryPick — strong block with the logo */}
        <Box sx={{ borderRadius: "16px", border: "1px solid rgba(230,29,80,0.55)", background: "linear-gradient(135deg, rgba(230,29,80,0.24), rgba(230,29,80,0.05))", p: "20px 24px", display: "flex", alignItems: "center", gap: "16px" }}>
          <Box sx={{ flexShrink: 0, width: 46, height: 46, borderRadius: "12px", background: "rgba(0,0,0,0.25)", border: "1px solid rgba(230,29,80,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}><img src={logoSite} alt="" width={26} /></Box>
          <Box>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: { xs: "17px", md: "19px" }, color: "#fff" }}>CherryPick your clips</Typography>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", color: "#f0d3dc" }}>Filter, sort &amp; tick the moments — you're in control.</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: "15px", color: "#8d94a8", textTransform: "uppercase", letterSpacing: "0.1em" }}>or</Box>
        {/* Chat with AI — strong block, chat-forward */}
        <Box sx={{ borderRadius: "16px", border: "1px solid rgba(192,31,90,0.7)", background: "linear-gradient(135deg,#3a0f22,#160b12)", p: "20px 24px", display: "flex", alignItems: "center", gap: "16px" }}>
          <Box sx={{ flexShrink: 0, width: 46, height: 46, borderRadius: "12px", background: "linear-gradient(140deg,#c01f5a,#7a1233)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><ChatBubbleOutlineIcon sx={{ fontSize: 24 }} /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: { xs: "17px", md: "19px" }, color: "#fff" }}>Or chat with CherryClipz AI</Typography>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", color: "#ecc7d6" }}>Tell Cherry the reel you want — it picks for you.</Typography>
          </Box>
          <Box component="button" onClick={scrollToDemo} sx={{ flexShrink: 0, cursor: "pointer", display: { xs: "none", sm: "flex" }, alignItems: "center", gap: "7px", border: "none", borderRadius: "10px", px: "16px", py: "11px", fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: "14px", color: "#fff", background: "linear-gradient(140deg,#c01f5a,#7a1233)", "&:hover": { filter: "brightness(1.12)" } }}><ChatBubbleOutlineIcon sx={{ fontSize: 17 }} /> Chat</Box>
        </Box>
      </Box>
    </Box>
    {/* The REAL editor, embedded inline (no click). Wider container so nothing clips on the edges. */}
    <Box id="cc-hero-demo" sx={{ width: "100%", maxWidth: "1680px", margin: "0 auto", mt: { xs: "18px", md: "28px" } }}>
      <HomeEtEmbed />
    </Box>
  </>
);

const VariantCMinimal: React.FC = () => (
  <Box sx={{ width: "100%", maxWidth: "860px", margin: "0 auto", padding: { xs: "16px 0 24px", md: "56px 0 32px" } }}>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: { xs: "18px", md: "26px" } }}>
      <img src={logoSite} alt="CherryClipz" width={52} style={{ height: "auto", display: "block" }} />
      <Typography sx={headlineSx("center", "xl")}>
        CherryPick your <A>video clipz.</A>
      </Typography>
      <Subhead align="center" max={620}>{SUBHEAD_LONG}</Subhead>
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: { xs: "4px", md: "8px" } }}>
        <UploadDownloadArea />
      </Box>
      <Pills center />
      <Typography sx={{ fontFamily: "Outfit, sans-serif", color: "#8d94a8", fontSize: "13px", textAlign: "center" }}>
        No signup needed to try — longer videos take a little longer to process.
      </Typography>
    </Box>
    <Box id="cc-hero-demo" sx={{ mt: { xs: "32px", md: "48px" } }}>
      <HeroClipDemo />
    </Box>
  </Box>
);

/* ---------------- New council variants D, E, F ---------------- */

/** D — "The product sells itself": benefit copy + upload LEFT, looping demo RIGHT. */
const VariantDDemoRight: React.FC = () => (
  <Box sx={{ width: "100%", maxWidth: "1320px", margin: "0 auto", padding: { xs: "8px 0 24px", md: "40px 0 48px" } }}>
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: "28px", md: "56px" } }}>
      <Box sx={{ flex: "1 1 45%", display: "flex", flexDirection: "column", gap: { xs: "18px", md: "24px" }, alignItems: { xs: "center", md: "flex-start" } }}>
        <Typography sx={{ ...headlineSx("left", "xl"), textAlign: { xs: "center", md: "left" } }}>
          Turn one video into a hundred <A>scroll-stoppers.</A>
        </Typography>
        <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
          <Subhead align="left">Upload once. We cut every sentence into a clip you can sort, score, and ship.</Subhead>
        </Box>
        <Box sx={{ width: "100%", maxWidth: "520px" }}>
          <UploadDownloadArea />
        </Box>
        <Pills />
      </Box>
      <Box id="cc-hero-demo" sx={{ flex: "1 1 55%", width: "100%" }}>
        <DemoFrame />
      </Box>
    </Box>
  </Box>
);

/** E — "Zero-friction two-CTA": try a sample first, or upload your own. Demo right. */
const VariantETwoCta: React.FC = () => (
  <Box sx={{ width: "100%", maxWidth: "1320px", margin: "0 auto", padding: { xs: "8px 0 24px", md: "40px 0 48px" } }}>
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: "28px", md: "56px" } }}>
      <Box sx={{ flex: "1 1 46%", display: "flex", flexDirection: "column", gap: { xs: "18px", md: "22px" }, alignItems: { xs: "center", md: "flex-start" } }}>
        <Typography sx={{ ...headlineSx("left", "xl"), textAlign: { xs: "center", md: "left" } }}>
          Stop scrubbing. <A>Start clipping.</A>
        </Typography>
        <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
          <Subhead align="left">Your whole video, cut into searchable, scorable clips in one pass.</Subhead>
        </Box>
        {/* Primary, zero-friction CTA. (Wiring it to open the editor on a sample reel is the
            next step — for now it jumps to the live demo.) */}
        <Box
          component="button"
          onClick={scrollToDemo}
          sx={{ cursor: "pointer", border: "none", borderRadius: "999px", backgroundColor: "#e61d50", color: "#fff", fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: { xs: "16px", md: "18px" }, padding: "14px 28px", "&:hover": { backgroundColor: "#c4123f" } }}
        >
          ▶ Try it with a sample reel
        </Box>
        <Typography sx={{ fontFamily: "Outfit, sans-serif", color: "#8d94a8", fontSize: "13px", mt: "-6px" }}>
          or upload your own —
        </Typography>
        <Box sx={{ width: "100%", maxWidth: "520px" }}>
          <UploadDownloadArea />
        </Box>
      </Box>
      <Box id="cc-hero-demo" sx={{ flex: "1 1 54%", width: "100%" }}>
        <DemoFrame />
      </Box>
    </Box>
  </Box>
);

/** F — "Cinematic glass": borrowed creator-photo grid backdrop, frosted CTA card left. */
const VariantFCinematic: React.FC = () => (
  <Box sx={{ position: "relative", width: "100%", minHeight: { xs: "auto", md: "calc(100vh - 120px)" }, overflow: "hidden", borderRadius: { xs: 0, md: "20px" } }}>
    {/* Photo grid backdrop (reused from the landing page) + dark scrim for legibility. */}
    <Box sx={{ position: "absolute", inset: 0, opacity: 0.28 }}>
      <HeroBackground />
    </Box>
    <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(12,11,16,0.96) 0%, rgba(12,11,16,0.82) 45%, rgba(12,11,16,0.5) 100%)" }} />
    <Box sx={{ position: "relative", zIndex: 2, display: "flex", justifyContent: { xs: "center", md: "flex-start" }, padding: { xs: "32px 16px", md: "72px 64px" } }}>
      <Box
        sx={{
          maxWidth: "560px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: { xs: "18px", md: "22px" },
          padding: { xs: "20px", md: "32px" },
          borderRadius: "20px",
          border: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(20,18,26,0.55)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Typography sx={headlineSx("left")}>
          The clips are already in your <A>footage.</A>
        </Typography>
        <Subhead align="left">We find every one and lay them out as a spreadsheet you can filter and export.</Subhead>
        <UploadDownloadArea />
        <Pills />
      </Box>
    </Box>
  </Box>
);

const MainSectionAlt: React.FC<{ variant: Variant }> = ({ variant }) => {
  switch (variant) {
    case "B":
      return <VariantBSplit />;
    case "C":
      return <VariantCMinimal />;
    case "D":
      return <VariantDDemoRight />;
    case "E":
      return <VariantETwoCta />;
    case "F":
      return <VariantFCinematic />;
    default:
      return <VariantBSplit />;
  }
};

export default MainSectionAlt;
